import { FC, useCallback, useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import TuneIcon from '@mui/icons-material/Tune'
import CloseIcon from '@mui/icons-material/Close'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { Box } from '@mui/system'
import StopBuzzerForm from './StopBuzzerForm'
import ObfuscationForm from './ObfuscationForm'
import { MAP_DATA } from './MapData'
import { startPlaybook } from '../../../services/playbook.service'
import { useToast } from '../../../contexts/toast.context'

export type DashboardFilter = {
  frequency_from: number | null
  frequency_to: number | null
  areas: string[]
}

interface IProps {
  onFilterChange: (filter: DashboardFilter) => void
}

const buildFilter: () => DashboardFilter = () => ({
  frequency_from: null,
  frequency_to: null,
  areas: [],
})

const ZONES = MAP_DATA.features.map((f) => ({
  id: f.properties.ULS_ID,
  label: f.properties.ULS_ID,
}))

const DashboardToolbar: FC<IProps> = ({ onFilterChange }) => {
  const [filterData, setFilterData] = useState<DashboardFilter>(() => buildFilter())
  const [stopBuzzerVisible, setStopBuzzerVisible] = useState<boolean>(false)
  const [obfuscationVisible, setObfuscationVisible] = useState<boolean>(false)
  const [stopBuzzerActive, setStopBuzzerActive] = useState<boolean>(false)
  const { showToast } = useToast()

  const handleStopBuzzerClick = useCallback(() => {
    setStopBuzzerVisible(true)
  }, [])

  const handleObfuscationClick = useCallback(() => {
    setObfuscationVisible(true)
  }, [])

  const handleClose = useCallback(() => {
    setStopBuzzerVisible(false)
    setObfuscationVisible(false)
  }, [])

  const handleStopBuzzerExecute = useCallback(() => {
    setStopBuzzerActive(true)
    setStopBuzzerVisible(false)
    setObfuscationVisible(false)
  }, [])

  const handleResumeTransmission = useCallback(async () => {
    try {
      await startPlaybook('stop-buzzer', { action: 'CANCEL' })
      setStopBuzzerActive(false)
      showToast('success', 'Transmission resumed — Stop Buzzer cleared')
    } catch {
      // axios interceptor already surfaced the error to the user
    }
  }, [showToast])

  useEffect(() => {
    // debouncing changes
    const timeoutId = setTimeout(() => onFilterChange.apply(null, [filterData]), 800)

    return () => clearTimeout(timeoutId)
  }, [filterData, onFilterChange])

  return (
    <>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <TextField
          label="Frequency from"
          type="number"
          value={filterData.frequency_from ?? ''}
          onChange={(e) =>
            setFilterData((prev) => ({
              ...prev,
              frequency_from: e.target.value !== '' ? Number(e.target.value) : null,
            }))
          }
          size="small"
          autoComplete="off"
          sx={{
            width: '200px',
            flexShrink: 0,
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">MHz</InputAdornment>,
          }}
        />

        <Typography fontWeight="bold">—</Typography>

        <TextField
          label="Frequency to"
          type="number"
          value={filterData.frequency_to ?? ''}
          onChange={(e) =>
            setFilterData((prev) => ({
              ...prev,
              frequency_to: e.target.value !== '' ? Number(e.target.value) : null,
            }))
          }
          size="small"
          autoComplete="off"
          sx={{
            width: '200px',
            flexShrink: 0,
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">MHz</InputAdornment>,
          }}
        />

        <FormControl sx={{ ml: 0, flexGrow: 2, flexBasis: '200px' }}>
          <InputLabel size="small">Geographic Zones</InputLabel>
          <Select
            value={filterData.areas}
            label="Geographic Zone"
            onChange={(e: SelectChangeEvent<string[]>) =>
              setFilterData((prev) => {
                let values: string[]

                if (typeof e.target.value === 'string') {
                  values = e.target.value.split(',')
                } else {
                  values = e.target.value
                }

                return {
                  ...prev,
                  areas: values,
                }
              })
            }
            size="small"
            multiple
            renderValue={(selected: string[]) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value: string) => (
                  <Chip key={value} label={value} size="small" sx={{ height: '1.785em' }} />
                ))}
              </Box>
            )}
          >
            {ZONES.map((zone) => (
              <MenuItem key={zone.id} value={zone.id}>
                <Checkbox checked={filterData.areas?.includes(zone.id)} />
                {zone.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" color="error" startIcon={<AddIcon />} onClick={handleStopBuzzerClick}>
          Stop Buzzer
        </Button>

        {stopBuzzerActive && (
          <Chip
            label="STOP BUZZER ACTIVE"
            color="error"
            size="small"
            sx={{
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              animation: 'pulse 1.4s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.45 },
              },
            }}
          />
        )}

        <Button variant="outlined" color="success" startIcon={<PlayArrowIcon />} onClick={handleResumeTransmission}>
          Resume Transmission
        </Button>

        <Button variant="contained" startIcon={<TuneIcon />} onClick={handleObfuscationClick}>
          Obfuscation
        </Button>
      </Stack>

      <Dialog
        open={stopBuzzerVisible || obfuscationVisible}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: '750px',
            maxWidth: 'none',
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 14,
            top: 10,
            color: (theme) => theme.palette.grey[500],
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        {stopBuzzerVisible && <StopBuzzerForm onCancel={handleClose} onExecute={handleStopBuzzerExecute} />}

        {obfuscationVisible && <ObfuscationForm onCancel={handleClose} />}
      </Dialog>
    </>
  )
}

export default DashboardToolbar
